'use strict';

const libPictProvider = require('pict-provider');
const libTemplate = require('../contract-admin/ContractAdmin-Template.js');

/**
 * ContractAdminProvisioning — GENERATES the canonical Contract Administration template on the HeadLight
 * customer (IDCustomer 1), and CLONES it — non-destructively + idempotently — into a chosen target customer.
 *
 * Replaces the retired, destructive #/ContractAdmin screen (which did full-replace Configuration/Put writes
 * against the operator's OWN customer). Every write here is safe and explicit-customer:
 *   - Report forms → PUT ReportNamedInstance/Upsert (upsert-by-GUID — idempotent, never duplicates).
 *   - Workflows    → create-if-absent (Configuration/Put/ApprovalWorkflow/<name>/Customer/<id>); never
 *                    overwrites a customer's existing/edited workflow.
 *   - Bindings     → read-merge-write the ApprovalWorkflowBindings 'Document' map (Configuration/Put is a
 *                    full-document REPLACE, so we merge our keys into the existing map and write it back).
 *
 * Runs as a HeadLight super-user (L5 / customer 1), who may write across customers with an explicit
 * IDCustomer — so no impersonation is needed.
 */
class ContractAdminProvisioningProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => pErr ? reject(pErr) : resolve(pBody))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pBody2) => pErr ? reject(pErr) : resolve(pBody2))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pBody2) => pErr ? reject(pErr) : resolve(pBody2))); }
	_unwrapConfig(pResult) { return (pResult && pResult.Config !== undefined) ? (pResult.Config || {}) : (pResult || {}); }
	_errText(pError) { return (pError && pError.message) ? pError.message : String(pError); }

	// ── Primitives (each targets an EXPLICIT IDCustomer) ─────────────────────────

	/** Upsert one ReportNamedInstance by its GUID — updates if the GUID exists, creates otherwise. */
	async _upsertFormAsync(pForm)
	{
		await this._putJSON('ReportNamedInstance/Upsert', pForm);
	}

	/**
	 * The customer's existing ApprovalWorkflow hashes, so workflows are created only when absent. Rejects when the read
	 * fails: an empty set here would make create-if-absent overwrite the customer's existing workflows.
	 */
	async _existingWorkflowHashesAsync(pIDCustomer)
	{
		const tmpMap = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/Customer/${pIDCustomer}`);
		return new Set(Object.keys(tmpMap || {}));
	}

	/** Create an ApprovalWorkflow blob only if the customer does not already have one under that name (never overwrite edits). */
	async _putWorkflowIfAbsentAsync(pIDCustomer, pHash, pBlob, pExistingSet)
	{
		if (pExistingSet && pExistingSet.has(pHash)) { return 'skipped'; }
		await this._postJSON(`Configuration/Put/ApprovalWorkflow/${encodeURIComponent(pHash)}/Customer/${pIDCustomer}`, pBlob);
		return 'created';
	}

	/**
	 * Merge {formHash: workflowName} pairs into the customer's ApprovalWorkflowBindings 'Document' map (read-merge-write; never
	 * full-replace). Returns the number of keys added/changed. Rejects when the read fails: merging into an empty map would
	 * write back only the template's keys and drop every other binding the customer has.
	 */
	async _mergeBindingsAsync(pIDCustomer, pBindings)
	{
		const tmpAll = await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/Customer/${pIDCustomer}`);
		const tmpDocMap = Object.assign({}, (tmpAll && tmpAll.Document) || {});
		let tmpAdded = 0;
		Object.keys(pBindings || {}).forEach((pFormHash) =>
		{
			if (tmpDocMap[pFormHash] !== pBindings[pFormHash]) { tmpDocMap[pFormHash] = pBindings[pFormHash]; tmpAdded++; }
		});
		if (tmpAdded > 0) { await this._postJSON(`Configuration/Put/ApprovalWorkflowBindings/Document/Customer/${pIDCustomer}`, tmpDocMap); }
		return tmpAdded;
	}

	// ── Materialize / Generate ───────────────────────────────────────────────────

	/**
	 * Materialize a template artifact set into a customer — idempotent + non-destructive.
	 * @param {number} pIDCustomer
	 * @param {Object} pTemplate  from ContractAdmin-Template.buildTemplate (or a clone source, re-stamped)
	 * @returns {Promise<Object>} per-item result summary
	 */
	async materializeTemplateAsync(pIDCustomer, pTemplate)
	{
		const tmpID = Number(pIDCustomer);
		const tmpResult = { IDCustomer: tmpID, Forms: [], Workflows: [], BindingsAdded: 0, Errors: [] };

		// 1) Forms — upsert-by-GUID.
		for (const tmpForm of (pTemplate.ReportForms || []))
		{
			try { await this._upsertFormAsync(tmpForm); tmpResult.Forms.push({ Hash: tmpForm.Hash, Status: 'upserted' }); }
			catch (pError) { tmpResult.Errors.push(`form ${tmpForm.Hash}: ${this._errText(pError)}`); tmpResult.Forms.push({ Hash: tmpForm.Hash, Status: 'error' }); }
		}

		// 2) Workflows — create-if-absent (preserve existing/edited workflows). Without the existing set we can't tell
		//    absent from present, so skip the step rather than overwrite.
		let tmpExisting = null;
		try
		{
			tmpExisting = await this._existingWorkflowHashesAsync(tmpID);
		}
		catch (pError)
		{
			tmpResult.Errors.push(`workflows: could not read the customer's existing workflows, so none were written: ${this._errText(pError)}`);
			(pTemplate.Workflows || []).forEach((pWF) => tmpResult.Workflows.push({ Hash: pWF.Hash, Status: 'skipped' }));
		}
		for (const tmpWF of (tmpExisting ? (pTemplate.Workflows || []) : []))
		{
			try { const tmpStatus = await this._putWorkflowIfAbsentAsync(tmpID, tmpWF.Hash, tmpWF.Blob, tmpExisting); tmpResult.Workflows.push({ Hash: tmpWF.Hash, Status: tmpStatus }); }
			catch (pError) { tmpResult.Errors.push(`workflow ${tmpWF.Hash}: ${this._errText(pError)}`); tmpResult.Workflows.push({ Hash: tmpWF.Hash, Status: 'error' }); }
		}

		// 3) Bindings — read-merge-write.
		try { tmpResult.BindingsAdded = await this._mergeBindingsAsync(tmpID, pTemplate.Bindings || {}); }
		catch (pError) { tmpResult.Errors.push(`bindings: ${this._errText(pError)}`); }

		return tmpResult;
	}

	/** Generate / refresh the canonical Contract-Admin template on the HeadLight customer (IDCustomer 1). */
	async generateHeadLightTemplateAsync()
	{
		const tmpTemplate = libTemplate.buildTemplate(libTemplate.HEADLIGHT_IDCUSTOMER);
		const tmpResult = await this.materializeTemplateAsync(libTemplate.HEADLIGHT_IDCUSTOMER, tmpTemplate);
		// (The module-catalog row is a client-side entry merged by the Customer workspace Modules tab — creating a
		// server Module row is a restricted op; config-app modules enable via a real CustomerModuleJoin by ModuleHash.)
		this.pict.log.info('ContractAdmin: generated HeadLight template', tmpResult);
		return tmpResult;
	}

	// ── Clone the HeadLight (cust 1) template into a target customer ──────────────

	/** Read the LIVE default-workflow blobs from HeadLight (cust 1) so admin tuning propagates; fall back to the canonical default. */
	async _readHeadLightWorkflowsAsync()
	{
		const tmpNames = [ libTemplate.PAYMENT_WORKFLOW, libTemplate.CHANGEORDER_WORKFLOW ];
		const tmpCanon = libTemplate.buildTemplate(libTemplate.HEADLIGHT_IDCUSTOMER).Workflows;
		const tmpResult = [];
		for (const tmpName of tmpNames)
		{
			let tmpBlob = null;
			try
			{
				const tmpRes = await this._postJSON('Configuration/Get', { LevelContext: 'Customer', LevelContextID: libTemplate.HEADLIGHT_IDCUSTOMER, NamespaceKey: 'ApprovalWorkflow', NamespaceValue: tmpName });
				const tmpUnwrapped = this._unwrapConfig(tmpRes);
				if (tmpUnwrapped && Array.isArray(tmpUnwrapped.States)) { tmpBlob = tmpUnwrapped; }
			}
			catch (pError) { /* fall back to the canonical default below */ }
			if (!tmpBlob) { const tmpFallback = tmpCanon.find((pW) => pW.Hash === tmpName); tmpBlob = tmpFallback ? tmpFallback.Blob : {}; }
			tmpResult.push({ Hash: tmpName, Blob: JSON.parse(JSON.stringify(tmpBlob)) });
		}
		return tmpResult;
	}

	/**
	 * Clone the HeadLight (cust 1) Contract-Admin template into a target customer — non-destructive + idempotent.
	 * Forms + bindings come from the canonical template (re-stamped for the target); workflows come from the LIVE
	 * cust-1 blobs (so HeadLight's own tuning of the defaults propagates). Returns the materialize result summary.
	 * @param {number} pIDCustomer
	 */
	async cloneToCustomerAsync(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		const tmpTemplate = libTemplate.buildTemplate(tmpID);
		tmpTemplate.Workflows = await this._readHeadLightWorkflowsAsync();
		return this.materializeTemplateAsync(tmpID, tmpTemplate);
	}

	// ── Capability grant (the real gate for the Contract-Admin screens) ──────────

	/** Find-or-create the customer's 'Customer Config Admin' permission group (PermissionLevel CustomerAdmin). */
	async _ensureConfigAdminGroupAsync(pIDCustomer)
	{
		const tmpName = 'Customer Config Admin';
		const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${Number(pIDCustomer)}~FBV~Name~EQ~${encodeURIComponent(tmpName)}~FBV~Deleted~EQ~0/0/1`).catch(() => null);
		const tmpExisting = (Array.isArray(tmpRoles) ? tmpRoles : []).find((pRole) => pRole && pRole.IDRole);
		if (tmpExisting) { return tmpExisting; }
		return this._postJSON('Role', { Name: tmpName, Type: 'PermissionGroup', PermissionLevel: 'CustomerAdmin', Description: 'Configuration-admin rights for this customer. Managed by the customer module tooling.', IDCustomer: Number(pIDCustomer) });
	}

	/** Grant the Module-ContractAdmin-Access capability to the customer's config-admin group, so the Contract-Admin screens become visible/usable for that customer. Returns the IDRole granted. */
	async grantCapabilityAsync(pIDCustomer)
	{
		const tmpGroup = await this._ensureConfigAdminGroupAsync(pIDCustomer);
		if (!tmpGroup || !tmpGroup.IDRole) { throw new Error('could not resolve the customer config-admin group'); }
		await this._getJSON(`Permissions/Grant/${encodeURIComponent(libTemplate.MODULE_ACCESS_CAPABILITY)}/To/Role/${Number(tmpGroup.IDRole)}/For/Customer/${Number(pIDCustomer)}`);
		return Number(tmpGroup.IDRole);
	}
}

module.exports = ContractAdminProvisioningProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ContractAdminProvisioning',
	AutoInitialize: true,
	AutoInitializeOrdinal: 45,
};
